import {FontAwesomeIcon as Icon} from '@fortawesome/react-fontawesome'
import _ from 'lodash'
import React, {createRef} from "react"
import ClickOutside from "./ClickOutside"

type SearchSelectProps = {
  value: string | number | null
  keyField: string
  labelField: string
  options: any
  filterFunction: (filter: string | null, options: any) => any
  setOption: (option: any) => void
  title: string
  pin?: string
  labelPrefix?: boolean
}

type SearchSelectState = {
  isOpen: boolean,
  search: string,
  highlightedIndex: number,
  filteredOptions: Array<any>
  selected: string | null
}

class SearchSelect extends React.Component<SearchSelectProps, SearchSelectState> {
  private searchRef = createRef<HTMLInputElement>()
  private refNodes: Array<HTMLLIElement> = []

  constructor(props: SearchSelectProps) {
    super(props)

    this.state = {
      isOpen: false,
      search: '',
      highlightedIndex: 0,
      filteredOptions: props.options,
      selected: null,
    }
  }

  componentDidMount = async () => {
    await this.setFilteredOptions()
    await this.select(this.props.value, false)
  }

  open = async () => {
    if (this.state.isOpen) {
      this.close()
      return
    }

    await this.setFilteredOptions()

    this.setState({
      isOpen: true
    })

    const index = _.findIndex(this.props.options, (option: any) => {
      return option[this.props.keyField] === this.props.value
    })

    await this.highlight(index >= 0 ? index : 0)

    this.scrollToHighlighted()
  }

  setFilteredOptions = async () => {
    await this.setState({
      search: this.searchRef.current ? this.searchRef.current.value : ''
    })

    const options = await this.props.filterFunction(this.state.search, this.props.options)

    this.setState({
      filteredOptions: options
    })
  }

  close = () => {
    if (!this.state.isOpen) {
      return
    }

    this.setState({
      isOpen: false
    })
  }

  select = async (option: string | number | null, set = true) => {
    if (set) {
      this.props.setOption && await this.props.setOption(option)
    }

    const selected = _.find(this.props.options, selectedOption => {
      return selectedOption[this.props.keyField] === this.props.value
    })

    let labelPrefix = null

    let selectedLabel = selected ? selected[this.props.labelField] : null

    if (selected && this.props.labelPrefix) {
      labelPrefix = this.props.labelPrefix ? (selected[this.props.keyField] + ': ') : null
    }

    if (labelPrefix && selectedLabel) {
      selectedLabel = labelPrefix + selectedLabel
    }

    await this.setState({
      search: '',
      highlightedIndex: 0,
      selected: selectedLabel
    })

    this.close()
  }

  selectHighlighted = async () => {
    const option = this.state.filteredOptions[this.state.highlightedIndex]
    await this.select(option ? option[this.props.keyField] : option)
  }

  scrollToHighlighted = () => {
    const element = _.get(this.refNodes, this.state.highlightedIndex)

    element && element.scrollIntoView({
      block: 'nearest',
    })
  }

  highlight = async (index: number) => {
    await this.setState({
      highlightedIndex: index
    })

    if (this.state.highlightedIndex < 0) {
      await this.setState({
        highlightedIndex: this.state.filteredOptions.length - 1
      })
    }

    if (this.state.highlightedIndex > this.state.filteredOptions.length - 1) {
      await this.setState({
        highlightedIndex: 0
      })
    }

    this.scrollToHighlighted()
  }

  highlightPrev = () => {
    this.highlight(this.state.highlightedIndex - 1)
  }

  highlightNext = () => {
    this.highlight(this.state.highlightedIndex + 1)
  }

  handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      this.highlightNext()
    }

    if (event.key === 'ArrowUp') {
      this.highlightPrev()
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      this.selectHighlighted()
    }

    if (event.key === 'Escape') {
      this.close()
    }

    if (event.key === 'Tab') {
      event.preventDefault()
      this.highlightNext()
    }
  }

  clearSelected = async (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    await this.select(null)
  }

  setRef = (node: HTMLLIElement) => {
    this.refNodes = [...this.refNodes, node]
  }

  render() {
    const {isOpen, search, highlightedIndex, filteredOptions, selected} = this.state
    const {title, keyField, labelField} = this.props
    return (
      <ClickOutside do={this.close}>
        <div className="relative">
          <button onClick={this.open} className="text-lg flex flex-row justify-between appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded px-4 py-2 leading-tight hover:bg-gray-200 focus:bg-gray-200 focus:border-gray-300 text-left focus:outline-none" ref="button" type="button">
            {
              selected ?
                <span className="font-semibold">{selected}
                  <span onClick={this.clearSelected}><Icon icon="times" className="text-base text-gray-500 ml-2"/></span>
                </span>
                :
                <span className="text-gray-500">{title}</span>
            }

            {
              isOpen ?
                <Icon icon="angle-up"/>
                :
                <Icon icon="angle-down"/>
            }
          </button>

          {
            isOpen ?
              <div className={"w-full bg-gray-200 text-gray-700 absolute z-10 shadow-lg " + (this.props.pin || '')}>
                <input placeholder="Suche" onKeyDown={this.handleKeyDown} className="appearance-none block w-full bg-gray-100 shadow text-gray-800 shadow rounded py-2 px-3 leading-tight hover:bg-gray-100 focus:bg-gray-100 focus:border-gray-300 focus:outline-none" ref={this.searchRef} value={search} onChange={this.setFilteredOptions}/>
                {
                  filteredOptions.length ?
                    <ul className="w-full h-full max-h-50 overflow-y-scroll scrollbar">
                      {_.map(filteredOptions, (option, i) => (
                        <li key={option[keyField]} onClick={() => this.select(option[keyField])} ref={this.setRef} className={"px-3 py-2 cursor-pointer hover:bg-gray-300" + (highlightedIndex === i ? ' bg-gray-300' : '')}>
                          {this.props.labelPrefix ?
                            <span>{option[keyField]}: </span>
                            : null}
                          {option[labelField]}
                        </li>
                      ))}
                    </ul>
                    : null
                }

                {!filteredOptions.length && search ?
                  <div className="px-2">Keine Ergebnisse für Suche: "{search}"</div>

                  : !filteredOptions.length ?
                    <div className="px-2">Es wurden keine Optionen gefunden</div>
                    : null
                }
              </div>
              : null
          }

        </div>
      </ClickOutside>
    )
  }
}

export default SearchSelect
